import { useState, useCallback, useMemo } from 'react';
import { getPresignedUploadUrl } from '../api';
import { computeFileChecksum } from '../utils/fileChecksum';

export interface UploadProgress {
  fileId: string;
  fileName: string;
  fileSize: number;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  mediaKey?: string;
  error?: string;
  /** SHA-256 hex of the file bytes; populated once known. */
  checksum?: string;
}

/**
 * A file already known to exist on the complaint (e.g. an attachment from a
 * previous comment). Used to dedup new uploads against historical media.
 */
export interface KnownChecksum {
  checksum: string;
  fileName: string;
}

interface UseMediaUploadOptions {
  maxFiles?: number;
  maxImageSizeBytes?: number;
  maxVideoSizeBytes?: number;
  allowedTypes?: string[];
  /**
   * Checksums already present on the complaint (e.g. uploaded in earlier
   * comments). New files matching these are rejected as duplicates.
   */
  existingChecksums?: KnownChecksum[];
}

const DEFAULT_OPTIONS: UseMediaUploadOptions = {
  maxFiles: 5,
  maxImageSizeBytes: 10 * 1024 * 1024, // 10MB for images
  maxVideoSizeBytes: 50 * 1024 * 1024, // 50MB for videos
  allowedTypes: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/heic',
    'image/heif',
    'video/mp4',
    'video/quicktime',
    'video/webm',
    'application/pdf',
  ],
};

/**
 * Hook for handling media file uploads with progress tracking
 * Files are uploaded directly to S3 using presigned URLs
 */
export function useMediaUpload(options: UseMediaUploadOptions = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  /**
   * Validate a file before upload
   */
  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file type
      if (!opts.allowedTypes?.includes(file.type)) {
        return `File type "${file.type}" is not allowed. Allowed types: images, videos, PDF`;
      }

      // Check file size based on type
      const isVideo = file.type.startsWith('video/');
      const maxSize = isVideo ? opts.maxVideoSizeBytes! : opts.maxImageSizeBytes!;
      if (file.size > maxSize) {
        const maxSizeMB = maxSize / (1024 * 1024);
        return `File size exceeds ${maxSizeMB}MB limit`;
      }

      return null;
    },
    [opts.allowedTypes, opts.maxImageSizeBytes, opts.maxVideoSizeBytes]
  );

  /**
   * Upload a file to S3 using XMLHttpRequest for progress tracking
   */
  const uploadToS3WithProgress = useCallback(
    async (
      url: string,
      file: File,
      onProgress: (progress: number) => void
    ): Promise<void> => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

        xhr.open('PUT', url);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
    },
    []
  );

  /**
   * Upload a single file
   */
  const uploadFile = useCallback(
    async (
      file: File,
      complaintId?: string,
      extraKnownChecksums?: KnownChecksum[],
      precomputedChecksum?: string | null
    ): Promise<string | null> => {
      const fileId = `${Date.now()}-${Math.random().toString(36).substring(7)}`;

      // Validate file
      const validationError = validateFile(file);
      if (validationError) {
        setUploads((prev) => [
          ...prev,
          {
            fileId,
            fileName: file.name,
            fileSize: file.size,
            progress: 0,
            status: 'error',
            error: validationError,
          },
        ]);
        return null;
      }

      // Compute checksum and reject if it duplicates a file already present
      const checksum =
        precomputedChecksum !== undefined
          ? precomputedChecksum
          : await computeFileChecksum(file);
      if (checksum) {
        const known = new Map<string, string>();
        for (const k of opts.existingChecksums || []) known.set(k.checksum, k.fileName);
        for (const k of extraKnownChecksums || []) known.set(k.checksum, k.fileName);
        // Files already in this hook's upload list (any non-error status)
        for (const u of uploads) {
          if (u.checksum && u.status !== 'error') known.set(u.checksum, u.fileName);
        }
        const conflict = known.get(checksum);
        if (conflict) {
          setUploads((prev) => [
            ...prev,
            {
              fileId,
              fileName: file.name,
              fileSize: file.size,
              progress: 0,
              status: 'error',
              error:
                conflict === file.name
                  ? `"${file.name}" is already attached.`
                  : `Duplicate of "${conflict}". This file is already attached.`,
              checksum,
            },
          ]);
          return null;
        }
      }

      // Add to uploads list as pending
      setUploads((prev) => [
        ...prev,
        {
          fileId,
          fileName: file.name,
          fileSize: file.size,
          progress: 0,
          status: 'pending',
          checksum: checksum || undefined,
        },
      ]);

      try {
        // Get presigned URL
        const response = await getPresignedUploadUrl({
          contentType: file.type,
          fileName: file.name,
          complaintId,
        });

        if (!response.status) {
          throw new Error('Failed to get upload URL');
        }

        // Update status to uploading
        setUploads((prev) =>
          prev.map((u) => (u.fileId === fileId ? { ...u, status: 'uploading' as const } : u))
        );

        // Upload to S3 with progress tracking
        await uploadToS3WithProgress(response.data.uploadUrl, file, (progress) => {
          setUploads((prev) =>
            prev.map((u) => (u.fileId === fileId ? { ...u, progress } : u))
          );
        });

        // Update status to completed
        setUploads((prev) =>
          prev.map((u) =>
            u.fileId === fileId
              ? {
                  ...u,
                  status: 'completed' as const,
                  progress: 100,
                  mediaKey: response.data.mediaKey,
                }
              : u
          )
        );

        return response.data.mediaKey;
      } catch (error) {
        setUploads((prev) =>
          prev.map((u) =>
            u.fileId === fileId
              ? {
                  ...u,
                  status: 'error' as const,
                  error: error instanceof Error ? error.message : 'Upload failed',
                }
              : u
          )
        );
        return null;
      }
    },
    [validateFile, uploadToS3WithProgress]
  );

  /**
   * Upload multiple files. Files are processed sequentially so that the
   * checksum dedup map (which lives in `uploads` state) sees each prior
   * file's checksum before validating the next one.
   */
  const uploadFiles = useCallback(
    async (
      files: File[],
      complaintId?: string,
      extraKnownChecksums?: KnownChecksum[]
    ): Promise<string[]> => {
      const currentCount = uploads.filter(
        (u) => u.status !== 'error'
      ).length;

      if (currentCount + files.length > opts.maxFiles!) {
        throw new Error(`Maximum ${opts.maxFiles} files allowed`);
      }

      setIsUploading(true);

      try {
        // Pre-compute every file's checksum once. We then use them for both
        // intra-batch dedup and pass each file's checksum into uploadFile so
        // it doesn't recompute.
        const checksums = await Promise.all(files.map((f) => computeFileChecksum(f)));
        const batchKnown: KnownChecksum[] = [...(extraKnownChecksums || [])];
        const results: (string | null)[] = [];
        for (let i = 0; i < files.length; i++) {
          const result = await uploadFile(files[i], complaintId, batchKnown, checksums[i]);
          if (result && checksums[i]) {
            batchKnown.push({ checksum: checksums[i] as string, fileName: files[i].name });
          }
          results.push(result);
        }
        return results.filter((key): key is string => key !== null);
      } finally {
        setIsUploading(false);
      }
    },
    [uploads, opts.maxFiles, uploadFile]
  );

  /**
   * Remove an upload from the list
   */
  const removeUpload = useCallback((fileId: string) => {
    setUploads((prev) => prev.filter((u) => u.fileId !== fileId));
  }, []);

  /**
   * Remove all uploads that failed validation or upload
   */
  const removeFailedUploads = useCallback(() => {
    setUploads((prev) => prev.filter((u) => u.status !== 'error'));
  }, []);

  /**
   * Clear all uploads
   */
  const clearUploads = useCallback(() => {
    setUploads([]);
  }, []);

  /**
   * Get all completed media keys
   */
  const completedMediaKeys = uploads
    .filter((u) => u.status === 'completed' && u.mediaKey)
    .map((u) => u.mediaKey!);

  /**
   * Map of completed mediaKey → checksum, for sending to the backend with
   * the complaint submission or comment payload.
   */
  const completedMediaChecksums = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of uploads) {
      if (u.status === 'completed' && u.mediaKey && u.checksum) {
        map[u.mediaKey] = u.checksum;
      }
    }
    return map;
  }, [uploads]);

  /**
   * Checksums of all files currently in the upload list (any non-error
   * status). Useful when external code needs to dedup its own uploads
   * (e.g. structured Bag/Case slots) against this hook's batch.
   */
  const knownChecksums = useMemo<KnownChecksum[]>(() => {
    const result: KnownChecksum[] = [];
    for (const u of uploads) {
      if (u.checksum && u.status !== 'error') {
        result.push({ checksum: u.checksum, fileName: u.fileName });
      }
    }
    return result;
  }, [uploads]);

  /**
   * Check if there are any errors
   */
  const hasErrors = uploads.some((u) => u.status === 'error');

  /**
   * Get count of files in each state
   */
  const uploadStats = {
    total: uploads.length,
    pending: uploads.filter((u) => u.status === 'pending').length,
    uploading: uploads.filter((u) => u.status === 'uploading').length,
    completed: uploads.filter((u) => u.status === 'completed').length,
    errors: uploads.filter((u) => u.status === 'error').length,
  };

  return {
    uploads,
    isUploading,
    uploadFile,
    uploadFiles,
    removeUpload,
    removeFailedUploads,
    clearUploads,
    completedMediaKeys,
    completedMediaChecksums,
    knownChecksums,
    hasErrors,
    uploadStats,
    maxFiles: opts.maxFiles,
  };
}
